import React, { useEffect, useState } from "react";
import { navLinks, profile } from "../mock";
import { Menu, X, Mail } from "lucide-react";

const NAV_OFFSET = 72;

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function smoothScrollTo(targetY, duration = 800) {
  const startY = window.scrollY;
  const distance = targetY - startY;
  if (Math.abs(distance) < 2) return;

  const reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (reduceMotion) {
    window.scrollTo({ top: targetY });
    return;
  }

  const startTime = performance.now();
  const step = (now) => {
    const elapsed = now - startTime;
    const t = Math.min(elapsed / duration, 1);
    window.scrollTo(0, startY + distance * easeInOutCubic(t));
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function scrollToHash(hash) {
  const id = hash.replace("#", "");
  if (!id || id === "top") {
    smoothScrollTo(0);
    return;
  }
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
  smoothScrollTo(top);
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToHash(href);
    if (window.history && window.history.replaceState) {
      window.history.replaceState(null, "", href);
    }
  };

  // Active section highlight
  useEffect(() => {
    const ids = navLinks.map((l) => l.href.replace("#", ""));
    const observers = ids
      .map((id) => {
        const el = document.getElementById(id);
        if (!el) return null;
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => {
              if (e.isIntersecting) setActiveId(id);
            });
          },
          { threshold: 0.4 }
        );
        io.observe(el);
        return io;
      })
      .filter(Boolean);
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[#09090b]/80 backdrop-blur border-b border-zinc-800"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-8 h-16 flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => handleNavClick(e, "#top")}
          className="flex items-center gap-2 group"
        >
          <span className="h-7 w-7 rounded-md bg-[#5eead4] text-[#09090b] grid place-items-center font-bold display">
            A
          </span>
          <span className="text-sm mono text-zinc-300 group-hover:text-white transition-colors">
            azzam.<span className="text-[#5eead4]">ai</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-7">
          {navLinks.map((l, i) => {
            const isActive = activeId === l.href.replace("#", "");
            return (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => handleNavClick(e, l.href)}
                className={`group text-sm transition-colors ${
                  isActive ? "text-white" : "text-zinc-400 hover:text-white"
                }`}
              >
                <span className="mono text-[#5eead4] mr-1.5">0{i + 1}.</span>
                {l.label}
              </a>
            );
          })}
        </nav>

        

        <button
          className="md:hidden text-zinc-300 p-2"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-zinc-800 bg-[#09090b]">
          <div className="px-6 py-4 flex flex-col gap-4">
            {navLinks.map((l, i) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => handleNavClick(e, l.href)}
                className="text-zinc-300 hover:text-[#5eead4] transition-colors"
              >
                <span className="mono text-[#5eead4] mr-2">0{i + 1}.</span>
                {l.label}
              </a>
            ))}
            <a
              href={profile.socials.email}
              className="btn-ghost rounded-md px-4 py-2 text-sm mono w-max inline-flex items-center gap-2"
            >
              <Mail size={14} /> Get in touch
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
